import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static values = {
    publicKey: String,
    clientSecret: String,
  }

  static targets = ['loading', 'error', 'container']

  async connect() {
    try {
      // Wait for Stripe.js to load before initializing
      await this.waitForStripe()

      // Hide loading indicator
      if (this.hasLoadingTarget) {
        this.loadingTarget.classList.add('hidden')
      }

      // Initialize Stripe Embedded Checkout
      this.stripe = Stripe(this.publicKeyValue)
      this.checkout = await this.stripe.initEmbeddedCheckout({
        clientSecret: this.clientSecretValue,
      })
      this.checkout.mount(this.containerTarget)
    } catch (error) {
      console.error('Failed to initialize Stripe checkout:', error)
      this.showError()
    }
  }

  disconnect() {
    // Signal to waitForStripe to stop polling
    this.disconnected = true

    // Clear polling interval if it exists
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval)
    }

    // Only destroy if checkout was successfully initialized
    if (this.checkout) {
      this.checkout.destroy()
    }
  }

  /**
   * Polls for Stripe.js availability with a timeout.
   * @param {number} timeout - Maximum wait time in milliseconds (default: 10000)
   * @returns {Promise<void>}
   * @throws {Error} If Stripe.js fails to load within the timeout period
   */
  waitForStripe(timeout = 10000) {
    return new Promise((resolve, reject) => {
      // Check if Stripe is already available
      if (typeof Stripe !== 'undefined') {
        return resolve()
      }

      const start = Date.now()
      this.pollingInterval = setInterval(() => {
        // Check if controller was disconnected
        if (this.disconnected) {
          clearInterval(this.pollingInterval)
          reject(new Error('Controller disconnected while waiting for Stripe.js'))
        } else if (typeof Stripe !== 'undefined') {
          clearInterval(this.pollingInterval)
          resolve()
        } else if (Date.now() - start > timeout) {
          clearInterval(this.pollingInterval)
          reject(new Error('Stripe.js failed to load within timeout period'))
        }
      }, 100) // Poll every 100ms
    })
  }

  /**
   * Shows error UI when Stripe initialization fails
   */
  showError() {
    // Hide loading indicator
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.add('hidden')
    }

    // Show error message
    if (this.hasErrorTarget) {
      this.errorTarget.classList.remove('hidden')
    }
  }

  /**
   * Reload the page to retry
   */
  retry() {
    window.location.reload()
  }
}
